/**
 * Portal sign-in. Same branding as the front desk, more polish — this is the
 * screen an owner sees on their phone.
 */
import { useState } from 'react';
import { getSupabase, isSupabaseConfigured } from '@shared/supabase';

export default function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    const supabase = getSupabase();
    if (!supabase) {
      setError('The cloud is not configured for this deployment.');
      setBusy(false);
      return;
    }
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) setError(authError.message);
    else onSignedIn();
    setBusy(false);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="SK Martial Arts Academy" className="mx-auto mb-5 h-24 w-auto" />
          <h1 className="text-3xl text-brand">SK MARTIAL ARTS</h1>
          <p className="mt-1 text-xs uppercase tracking-button text-white/40">
            Management Portal
          </p>
        </div>

        {!isSupabaseConfigured ? (
          <div className="card p-6 text-sm text-white/50">
            This deployment has no cloud connection configured. See SETUP.md.
          </div>
        ) : (
          <form onSubmit={submit} className="card space-y-4 p-6">
            <div>
              <label className="label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                className="field"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                className="field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <p className="rounded-btn border border-status-expired/40 bg-status-expiredBg p-3 text-sm text-status-expired">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className="btn w-full">
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs leading-relaxed text-white/25">
          The portal is read-only. Members and payments are recorded at the front desk.
        </p>
      </div>
    </div>
  );
}
